import { takeEvery, select, all, put } from 'redux-saga/effects'

import {
  addOutputNode,
  deleteOutputNode,
  setOutputField,
} from 'actions/feature/outputs'
import { setFunctionField } from 'actions/feature/functions'
import { setQueryField } from 'actions/feature/queries'
import { MERGE_DONOR_LOGIC } from 'actions/feature/features'

import {
  outputMapsSelector,
  featureOutputsSelector,
} from 'selectors/feature/outputs'
import { allFunctionsSelector } from 'selectors/feature/functions'

import { makeTempId } from 'utils/identity'

function* handleMergeDonorLogic({ donorLogicMap, currentFeatureId }) {
  // the function graphs that are displayed to the user have a dependent
  // relationship between outputs and functions. Specifically, the outputMaps
  // It is possible during batch import that a user may want to preserve a
  // function graph but have a different output. This saga is handling that
  // use-case (with a side effect of getting rid of other unwanted outputs)
  const [outputMaps] = yield all([select(outputMapsSelector)])

  const outputs = donorLogicMap.get('outputs')
  const functionCleanup = donorLogicMap
    .get('functions')
    .reduce((accum, funcUri) => {
      const fnOutputs = outputMaps.getIn([funcUri, 'outputs'])
      const removedOutputs = fnOutputs.reduce(
        (acc, outputId) =>
          // any outputs that will be omitted from the merge
          !outputs.includes(outputId)
            ? [
                ...acc,
                // are provided 'delete' actions
                put(deleteOutputNode(outputId, funcUri)),
              ]
            : acc,
        []
      )
      if (removedOutputs.length === fnOutputs.size) {
        // if all of a function's outputs are to be omitted from the merge,
        // we need to have a blank output in place to preserve the function
        // graph drawing logic.
        const tempId = makeTempId('output')
        return [
          ...accum,
          ...removedOutputs,
          put(addOutputNode(undefined, funcUri, tempId, 0)),
        ]
      }

      if (removedOutputs.length > 0) {
        return [...accum, ...removedOutputs]
      }
      return accum
    }, [])

  yield all(functionCleanup)

  // PBI-492348: Append '.1' to any imported function description that duplicates
  // an existing function description in the current feature
  const allFunctionsDict = yield select(allFunctionsSelector)
  const featureFunctionUris = yield select((state) =>
    state.getIn([
      'feature',
      'currentFeature',
      'feature',
      currentFeatureId,
      'functions',
    ])
  )

  const donorFunctions = donorLogicMap.get('functions')

  const existingFunctionDescriptions = featureFunctionUris
    .filter((uri) => !donorFunctions.includes(uri))
    .map((uri) => allFunctionsDict.getIn([uri, 'description']))
    .filter((desc) => desc != null)
    .toSet()

  const functionRenameActions = donorFunctions.reduce((acc, uri) => {
    const description = allFunctionsDict.getIn([uri, 'description'])
    if (description && existingFunctionDescriptions.has(description)) {
      return [
        ...acc,
        put(setFunctionField(uri, 'description', `${description}.1`)),
      ]
    }
    return acc
  }, [])

  // Append '.1' to any imported output description that duplicates an existing one
  const allOutputsDict = yield select(featureOutputsSelector)
  const donorOutputs = donorLogicMap.get('outputs')

  const existingOutputDescriptions = allOutputsDict
    .filter((_, id) => !donorOutputs.includes(id))
    .map((output) => output.get('description'))
    .filter((desc) => desc != null)
    .toSet()

  const outputRenameActions = donorOutputs.reduce((acc, id) => {
    const description = allOutputsDict.getIn([id, 'description'])
    if (description && existingOutputDescriptions.has(description)) {
      return [
        ...acc,
        put(setOutputField('description', `${description}.1`, id)),
      ]
    }
    return acc
  }, [])

  // Append '.1' to any imported logic set / timer description that duplicates an existing one
  const queriesDict = yield select((state) =>
    state.getIn(['feature', 'currentFeature', 'queries'])
  )
  const donorQueries = donorLogicMap.get('queries')

  const existingQueryDescriptions = queriesDict
    .filter((_, uri) => !donorQueries.includes(uri))
    .map((query) => query.get('description'))
    .filter((desc) => desc != null)
    .toSet()

  const queryRenameActions = donorQueries.reduce((acc, uri) => {
    const description = queriesDict.getIn([uri, 'description'])
    if (description && existingQueryDescriptions.has(description)) {
      return [
        ...acc,
        put(setQueryField(uri, 'description', `${description}.1`)),
      ]
    }
    return acc
  }, [])

  const allRenameActions = [
    ...functionRenameActions,
    ...outputRenameActions,
    ...queryRenameActions,
  ]

  if (allRenameActions.length > 0) {
    yield all(allRenameActions)
  }
}

export default function* watchMergeDonorLogic() {
  yield takeEvery(MERGE_DONOR_LOGIC, handleMergeDonorLogic)
}
